import React, { forwardRef } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Collapse,
} from "@mui/material";

// ==============================================
// FUNCIONES INTERNAS (AUTOCONTENIDAS)
// ==============================================

// Función utilitaria para extraer valor numérico de precios
const parsePrice = (priceString) => {
  if (!priceString || priceString === "Gratis" || priceString === "Gratuito") return 0;
  // Eliminar símbolos de moneda, espacios y puntos, mantener solo números
  const numericValue = priceString.replace(/[^\d]/g, '');
  return parseInt(numericValue) || 0;
};

const transformModalidad = (modalidad) => {
  const modalidades = {
    consultorio: "Consulta presencial",
    domicilio: "Consulta a domicilio",
    online: "Consulta online",
  };

  return modalidades[modalidad] || modalidad;
};

// ==============================================
// COMPONENTE PRINCIPAL DE LA SECCIÓN
// ==============================================

const SeccionServicios = forwardRef(({ 
  profesionalInfo, 
  expandedService, 
  setExpandedService, 
  handleAgendarClick,
  isMobile,
  serviceCardsRef,
  ordenServicios = ['online', 'presencial', 'domicilio'] // Valor por defecto
}, ref) => {
  
  const handleExpandClick = (serviceId) => {
    setExpandedService(expandedService === serviceId ? null : serviceId);
  };

  const renderServiceCards = () => {
    if (!profesionalInfo) {
      return (
        <Typography sx={{ fontFamily: "Quicksand-Regular", color: "#9f9f9f", fontSize: { md: "16px", xs: "14px" }, mb: 0 }}>
          No se encontró información del profesional.
        </Typography>
      );
    }

    const { servicio_consulta, servicio_domicilio, servicio_remoto } =
      profesionalInfo;

    if (!servicio_consulta && !servicio_domicilio && !servicio_remoto) {
      return (
        <Typography sx={{ fontFamily: "Quicksand-Regular", color: "#9f9f9f", fontSize: { md: "16px", xs: "14px" }, mb: 0 }}>
          
        </Typography>
      );
    }

    const renderServiceSection = (title, serviceType, services) => {
      if (!services || services.length === 0) return null;

      // Ordenar servicios por posicion_relativa (mayor primero), desempatando por nombre
      const sortedServices = [...services].sort((a, b) => {
        const posA = a.posicion_relativa ?? 0;
        const posB = b.posicion_relativa ?? 0;
        
        if (posB !== posA) {
          return posB - posA; // Orden descendente (mayor posición arriba)
        }
        
        // Desempate por nombre (orden alfabético)
        const nameA = a.servicio_nombre || "";
        const nameB = b.servicio_nombre || "";
        return nameA.localeCompare(nameB);
      });
    
      return (
        <Box sx={{ mb: 4 }}>
          <Typography
            sx={{ fontFamily: "Quicksand-Bold", fontSize: { md: "18px", xs: "16px" }, mb: 2 }}
          >
            {title}
          </Typography>
    
          {serviceType === "consulta" && profesionalInfo.cobertura_consulta && (
            <Typography sx={{ fontFamily: "Quicksand-Regular", mb: 2, mt: -2, fontSize: { md: "16px", xs: "14px" } }}>
              Atención presencial en: {profesionalInfo.cobertura_consulta}
              {profesionalInfo.cobertura_consulta_referencia &&
                ` - ${profesionalInfo.cobertura_consulta_referencia}`}
            </Typography>
          )}
    
          {serviceType === "domicilio" &&
            profesionalInfo.cobertura_domicilio && (
              <Typography sx={{ fontFamily: "Quicksand-Regular", mb: 2, mt: -2,  fontSize: { md: "16px", xs: "14px" } }}>
                Atención a domicilio en:{" "}
                {profesionalInfo.cobertura_domicilio.join(", ")}
              </Typography>
            )}
    
          {sortedServices.map((servicio) => (
            <Card
              key={servicio.id_servicio_fantasma}
              sx={{ mb: 2, borderRadius: 2, boxShadow: 3 }}
            >
              <CardContent>
                <Typography
                  sx={{ fontFamily: "Quicksand-Bold", fontSize: { md: "16px", xs: "14px" }, mb: 0 }}
                >
                  {servicio.servicio_nombre}
                </Typography>
                <Box
                  sx={{
                    display: "inline-block",
                    backgroundColor: "#f0f4ff",
                    color: "#3B82F6",
                    fontFamily: "Quicksand-Medium",
                    fontSize: { md: "15px", xs: "13px" },
                    borderRadius: 2,
                    paddingLeft: 0.75,
                    paddingRight: 0.75,
                    paddingTop: 0.25,
                    paddingBottom: 0.25,
                    mb: 0.25,
                    marginLeft: -0.75,
                    textAlign: "left",
                  }}
                >
                  {transformModalidad(servicio.servicio_modalidad)}
                </Box>
                <Typography 
                  sx={{ fontFamily: "Quicksand-Regular", color: "#626262", fontSize: { md: "15px", xs: "13px" }, mb: 0 }}
                >
                  {servicio.servicio_duracion}
                </Typography> 
                <Typography
                  sx={{ fontFamily: "Quicksand-Bold", color: "#3B82F6", fontSize: { md: "16px", xs: "14px" }, mb: 0 }}
                >
                  {servicio.servicio_precio}
                </Typography>
                {isMobile ? (
                  <>
                    <Button
                      variant="contained"
                      disableRipple
                      disableElevation
                      disableFocusRipple
                      onClick={() => handleAgendarClick(servicio)}
                      sx={{
                        fontFamily: "Quicksand-Bold",
                        backgroundColor: "#3B82F6",
                        "&:hover": { 
                          backgroundColor: "#3B82F6",
                          boxShadow: "none !important",
                        },
                        "&:active": {
                          backgroundColor: "#3B82F6",
                          boxShadow: "none !important",
                        },
                        "&:focus": {
                          backgroundColor: "#3B82F6",
                          boxShadow: "none !important",
                        },
                        borderRadius: 2,
                        width: "100%", // Ancho completo solo en móvil
                        py: 1, // Padding vertical
                        mt: 1,
                        mb: 1, // Margen inferior
                        fontSize: { md: "16px", xs: "14px" },
                        boxShadow: "none !important",
                        transition: "none !important",
                        "& .MuiTouchRipple-root": {
                          display: "none",
                        },
                      }}
                    >
                      Agendar
                    </Button>
    
                    {/* Texto centrado debajo del botón solo en móvil */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        mt: 1,
                        mb: 0,
                      }}
                    >
                      <Typography
                        onClick={() =>
                          handleExpandClick(servicio.id_servicio_fantasma)
                        }
                        sx={{
                          fontFamily: "Quicksand-Bold",
                          color: "#3B82F6",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          fontSize: { md: "14px", xs: "12px" },
                          mb: 0,
                        }}
                      >
                        Ver descripción y condiciones
                      </Typography>
                    </Box>
                  </>
                ) : (
                  // Versión original para escritorio
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mt: 1,
                      mb: 0,
                    }}
                  >
                    <Typography
                      onClick={() =>
                        handleExpandClick(servicio.id_servicio_fantasma)
                      }
                      sx={{
                        fontFamily: "Quicksand-Bold",
                        color: "#3B82F6",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        fontSize: { md: "14px", xs: "12px" },
                        mb: 0,
                      }}
                    >
                      Ver descripción y condiciones
                    </Typography>
                    <Button
                      variant="contained"
                      disableRipple
                      disableElevation
                      disableFocusRipple
                      onClick={() => handleAgendarClick(servicio)}
                      sx={{
                        fontFamily: "Quicksand-Bold",
                        backgroundColor: "#3B82F6",
                        "&:hover": { 
                          backgroundColor: "#3B82F6",
                          boxShadow: "none !important",
                        },
                        "&:active": {
                          backgroundColor: "#3B82F6",
                          boxShadow: "none !important",
                        },
                        "&:focus": {
                          backgroundColor: "#3B82F6",
                          boxShadow: "none !important",
                        },
                        borderRadius: 2,
                        fontSize: { md: "16px", xs: "14px" },
                        boxShadow: "none !important",
                        transition: "none !important",
                        "& .MuiTouchRipple-root": {
                          display: "none",
                        },
                      }}
                    >
                      Agendar
                    </Button>
                  </Box>
                )}
                <Collapse
                  in={expandedService === servicio.id_servicio_fantasma}
                >
                  {servicio.servicio_descripcion
                    .split("\n")
                    .filter((parrafo) => parrafo.trim() !== "")
                    .map((parrafo, index) => (
                      <Typography
                        key={index}
                        sx={{
                          fontFamily: "Quicksand-Regular",
                          color: "#626262",
                          fontSize: { md: "16px", xs: "14px" },
                          pt: 1,
                          mb: 0,
                        }}
                      >
                        {parrafo}
                      </Typography>
                    ))}
                </Collapse>
              </CardContent>
            </Card>
          ))}
        </Box>
      );
    };

    // NUEVO: Mapeo de identificadores a funciones de renderizado
    const modalidadesMap = {
      'online': () => servicio_remoto && renderServiceSection(
        "Consulta online",
        "remoto",
        profesionalInfo.servicios.filter((s) => s.servicio_modalidad === "online")
      ),
      'presencial': () => servicio_consulta && renderServiceSection(
        "Consulta presencial",
        "consulta",
        profesionalInfo.servicios.filter((s) => s.servicio_modalidad === "consultorio")
      ),
      'domicilio': () => servicio_domicilio && renderServiceSection(
        "Consulta a domicilio",
        "domicilio",
        profesionalInfo.servicios.filter((s) => s.servicio_modalidad === "domicilio")
      ),
    };

    // NUEVO: Renderizar según orden dinámico
    return (
      <div ref={serviceCardsRef}>
        {ordenServicios
          .filter(modalidad => modalidadesMap[modalidad]) // Solo modalidades válidas
          .map(modalidad => (
            <React.Fragment key={modalidad}>
              {modalidadesMap[modalidad]()}
            </React.Fragment>
          ))
        }
      </div>
    );
  };

  return (
    <>
      {/* 3. SERVICIOS */}
      <Box ref={ref} sx={{ mt: { xs: 3, md: 3 } }}>
        {renderServiceCards()}
      </Box>
    </>
  );
});

export default SeccionServicios;
